import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CustomerSpecialRequirement } from 'src/app/shared/models/data/enquiry.model';
import { BackendService } from 'src/app/shared/services/backend/backend.service';
import { SelectOption } from 'src/app/shared/teiasoft-bucket/teiasoft-select/teiasoft-select.component';
import { TranslateService } from 'src/app/translate.service';
import { Subscription } from 'rxjs';
import _ from 'lodash';


@Component({
  selector: 'app-modal-special-requirements',
  templateUrl: './modal-special-requirements.component.html',
  styleUrl: './modal-special-requirements.component.scss'
})
export class ModalSpecialRequirementsComponent implements OnInit, OnDestroy {

  @Input() readOnly: boolean = true;
  @Input() initialCustomerSpecialRequirements: CustomerSpecialRequirement[] = new Array();
  @Input() isMobile: boolean = false;


  @Output() onClose: EventEmitter<void> = new EventEmitter<void>();
  @Output() onSave: EventEmitter<CustomerSpecialRequirement[]> = new EventEmitter<CustomerSpecialRequirement[]>();

  title: string = '';
  customerSpecialRequirements: CustomerSpecialRequirement[] = new Array();
  itdcrMillOptionList: SelectOption[] = new Array();
  subscriptions: Subscription = new Subscription();

  constructor(private translate: TranslateService, private backendService: BackendService) { }

  ngOnInit(): void {
    // clone to avoid mutating the input array
    const cloned = (this.initialCustomerSpecialRequirements ?? []).map(x => ({
      ...x,
      isCustomerRelevant: !!x.isCustomerRelevant,
      itdcrMillId: x.itdcrMillId != null ? Number(x.itdcrMillId) : undefined
    }));
    // sort by rowNumber ascending
    cloned.sort((a, b) => (a.rowNumber ?? 0) - (b.rowNumber ?? 0));
    this.customerSpecialRequirements = cloned;

    this.title = this.translate.istant('enquiryBook.customerSpecialRequirements');

    if (this.customerSpecialRequirements.length > 0) {
      this.title += ` (${this.customerSpecialRequirements.length})`;
    }

    this.loadItdcrMills();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onSaveClick(): void {
    // emit a sorted copy
    const payload = this.customerSpecialRequirements
      .map(x => ({
        ...x,
        isCustomerRelevant: !!x.isCustomerRelevant,
        itdcrMillId: x.itdcrMillId != null ? Number(x.itdcrMillId) : undefined
      }))
      .sort((a, b) => (a.rowNumber ?? 0) - (b.rowNumber ?? 0));
    this.onSave.emit(payload);
  }

  onCloseClick() {
    this.onClose.emit();
  }

  onSalesPersonCommentInput(event: any, index: number): void {
    const { value } = event.target;
    const row = this.customerSpecialRequirements[index];
    if (!row) return;
    this.customerSpecialRequirements[index] = {
      ...row,
      salesPersonComment: value
    };
  }

  onCustomerRelevantChange(event: any, index: number): void {
    const { checked } = event.target;
    const row = this.customerSpecialRequirements[index];
    if (!row) return;
    this.customerSpecialRequirements[index] = {
      ...row,
      isCustomerRelevant: !!checked
    };
  }

  onItdcrMillChange(event: SelectOption, index: number): void {
    const row = this.customerSpecialRequirements[index];
    if (!row) return;

    const selectedValue = event?.value;
    let parsedValue: number | undefined = undefined;
    if (selectedValue != null && selectedValue !== '') {
      const numericValue = Number(selectedValue);
      parsedValue = Number.isFinite(numericValue) ? numericValue : undefined;
    }

    this.customerSpecialRequirements[index] = {
      ...row,
      itdcrMillId: parsedValue
    };
  }

  addRequirement(): void {
    if (this.readOnly) return;
    const nextRowNumber =
      (this.customerSpecialRequirements.reduce((m, x) => Math.max(m, x.rowNumber ?? 0), 0) || 0) + 1;

    this.customerSpecialRequirements = [
      ...this.customerSpecialRequirements,
      { rowNumber: nextRowNumber, salesPersonComment: '', millComment: '', isCustomerRelevant: false, itdcrMillId: undefined }
    ];
    this.resort();
  }

  removeRequirement(index: number): void {
    if (this.readOnly) return;
    const copy = [...this.customerSpecialRequirements];
    copy.splice(index, 1);
    // re-number to keep them tidy (1..n)
    copy.forEach((x, i) => (x.rowNumber = i + 1));
    this.customerSpecialRequirements = copy;
  }

  duplicateRequirement(index: number): void {
    if (this.readOnly) return;
    let row = _.cloneDeep(this.customerSpecialRequirements[index]); // TODO: avoid direct mutation and clean the millComment and itdcrMillId in 
    if (!row) return;

    row.millComment = ''; // reset mill comment on duplicate to avoid sharing it between rows
    row.itdcrMillId = undefined; // reset mill selection on duplicate to avoid sharing it between rows


    const nextRowNumber =
      (this.customerSpecialRequirements.reduce((m, x) => Math.max(m, x.rowNumber ?? 0), 0) || 0) + 1;

    this.customerSpecialRequirements = [
      ...this.customerSpecialRequirements,
      { ...row, rowNumber: nextRowNumber }
    ];
    this.resort();
  }

  trackByIndex(index: number): number {
    return index;
  }

  get canSave(): boolean {
    return this.customerSpecialRequirements.every(x => {
      if (!this.isItdcrMillSelected(x)) return false;
      const salesComment = (x.salesPersonComment ?? '').trim();
      if (salesComment.length > 0) return true;
      return (x.millComment ?? '').trim().length > 0;
    });
  }

  get canAdd(): boolean {
    if (this.customerSpecialRequirements.length === 0) {
      return true; // if no rows, allow adding
    }
    const last = this.customerSpecialRequirements[this.customerSpecialRequirements.length - 1];
    return (last.salesPersonComment ?? '').trim().length > 0 || (last.millComment ?? '').trim().length > 0;
  }

  private resort(): void {
    this.customerSpecialRequirements.sort((a, b) => (a.rowNumber ?? 0) - (b.rowNumber ?? 0));
  }

  private loadItdcrMills(): void {
    this.subscriptions.add(this.backendService.getItdcrMills().subscribe(res => {
      const uniqueItdcrMills = new Map<number, SelectOption>();

      for (const itdcrMill of (res?.itdcrMills ?? [])) {
        const itdcrMillId = itdcrMill?.itdcrMillId;
        if (itdcrMillId == null || uniqueItdcrMills.has(itdcrMillId)) continue;

        const option = new SelectOption();
        option.value = itdcrMillId;
        option.text = (itdcrMill.itdcrMillName ?? itdcrMill.itdcrMillCode ?? itdcrMillId.toString()).toString();
        uniqueItdcrMills.set(itdcrMillId, option);
      }

      this.itdcrMillOptionList = Array.from(uniqueItdcrMills.values())
        .sort((a, b) => a.text.toString().localeCompare(b.text.toString()));
    }));
  }

  isItdcrMillSelected(item: CustomerSpecialRequirement): boolean {
    return item?.itdcrMillId != null && Number.isFinite(Number(item.itdcrMillId));
  }

}
